/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { v4 as uuidV4 } from 'uuid';
import Store from '../store/store';
import { setAuth, userGot401 } from '../actions/userActions';
import Types from '../constants/actionTypes';

// this is a queue that is used to keep track of all pending api requests;
// each api requst tracker gets deleted upon request completion or error occurs
const inflightRequests = {};

/*
  The following generateController function is invoked for each api request. 
  The purpose of it is to allow us to cancel pending api requests individually when needed.
*/
const generateController = () => {
  const controller = new AbortController();
  const { signal } = controller;
  const controllerKey = uuidV4();

  // store each controller generated to later be deleted when the api request returns.
  inflightRequests[controllerKey] = controller;

  return { signal, controllerKey };
};

const abortAndClearCache = () => {
  Object.keys(inflightRequests).forEach((controllerKey) => {
    inflightRequests[controllerKey].abort();
    delete inflightRequests[controllerKey];
  });
};
const baseUrl = process.env.HOST_BASE_URL;
const domain = `${baseUrl}/api`;

const requestHeaders = () => {
  const userDetails = (Store && Store.getState().userReducer.user) || {
    uid: null,
    expiry: null,
    client: null,
    token: null
  };

  const browserId =
    (Store && Store.getState().defaultOptionsReducer.browserId) || '';
  const csrfTokenElement = document.querySelector('meta[name="csrf-token"]');
  const csrfToken =
    csrfTokenElement && csrfTokenElement.getAttribute('content');

  let selectedTeamSpaceId = [];
  let selectedProductSpaceId = [];

  if (window.localStorage.selectedSpace) {
    const space = JSON.parse(window.localStorage.getItem('selectedSpace'));
    if (space.type === 'product') {
      selectedProductSpaceId = [space.id];
    } else if (space.type === 'team') {
      selectedTeamSpaceId = [space.id];
    }
  }

  return {
    'X-CSRF-Token': csrfToken,
    'content-type': 'application/json',
    'access-token': userDetails.token || '',
    'token-type': 'Bearer',
    client: userDetails.client || '',
    expiry: userDetails.expiry || '',
    uid: userDetails.uid || '',
    'current-team-ids': selectedTeamSpaceId,
    'current-product-ids': selectedProductSpaceId,
    'browser-id': browserId
  };
};

const encodeParams = (data) => {
  const esc = encodeURIComponent;
  let query = '';
  if (data) {
    query = Object.keys(data)
      .map((key) => {
        let params = '';
        if (typeof data[key] === 'object') {
          params = Object.keys(data[key]).map(
            (innerKey) => `${esc(innerKey)}=${esc(data[key][innerKey])}`
          );
        } else {
          params = `${esc(key)}=${esc(data[key])}`;
        }
        return params;
      })
      .join('&');
  }
  return query;
};

const defaultUnprocessableHandler = (errorResponse) => {
  const error = new Error('Unprocessable entity');
  error.errors = errorResponse;
  // eslint-disable-next-line no-console
  console.error('Unprocessable entity', errorResponse);
  return Promise.reject(error);
};

const checkStatus = (
  response,
  controllerKey,
  handleUnprocessable = undefined
) => {
  if (response.ok) {
    delete inflightRequests[controllerKey];
    const { notificationReducer } = Store.getState();

    if (
      notificationReducer.showNotification &&
      notificationReducer.notificationType === 'lostConnection'
    ) {
      Store.dispatch({ type: Types.HIDE_NOTIFICATION });
    }

    return response;
  }
  let error;

  if (response.status === 500) {
    delete inflightRequests[controllerKey];
    error = new Error(response.statusText);
    return Promise.reject(error);
  }

  error = new Error(response.statusText);

  return response.json().then((body) => {
    const errorResponse =
      Object.keys(body).length > 0 ? body[Object.keys(body)[0]] : null;
    if (response.status === 401) {
      if (body.errors && body.errors.length > 0) {
        error.message = body.errors.join('\n');
      }

      if (!response.url.includes('sign_in')) {
        Store.dispatch(userGot401());
      }

      abortAndClearCache();
      return Promise.reject(401);
    }

    delete inflightRequests[controllerKey];
    if (response.status === 403) {
      if (body.errors && body.errors.length > 0) {
        error.message = body.errors.join('\n');
      }
      return Promise.reject(error);
    }
    if (response.status === 405 && !!errorResponse) {
      return {
        status: 405,
        message: errorResponse
      };
    }
    if (typeof errorResponse === 'string') {
      error.message = errorResponse;
    } else if (
      errorResponse !== null &&
      typeof errorResponse === 'object' &&
      errorResponse.status === 400
    ) {
      error = errorResponse;
    } else {
      error.message = errorResponse ? errorResponse[0] : error.message;
    }
    if (response.status === 302) {
      // include new route for redirect responses from endpoints
      window.location.href = baseUrl + body.route;
      return;
    }

    if (response.status === 422 && handleUnprocessable) {
      return handleUnprocessable(errorResponse);
    }

    if (typeof body.errors === 'object') error.errors = body.errors;
    console.error(error.stack, response);

    return Promise.reject(error);
  });
};

const setAuthFromHeaders = (response) => {
  if (response.ok || response.status === 401 || response.status === 403) {
    const uid = response.headers.get('uid');
    const client = response.headers.get('client');
    const token = response.headers.get('access-token');
    const expiry = response.headers.get('expiry');
    Store.dispatch(
      setAuth({
        uid,
        client,
        token,
        expiry
      })
    );
  }
  return response;
};

export const get = (endpointUrl, data, encode=true) => {
  const { signal, controllerKey } = generateController();

  const params = data ? (encode ? encodeParams(data) : data) : '';
  const request = {
    method: 'GET',
    headers: requestHeaders(),
    credentials: 'include',
    signal
  };
  // console.log(`GET ${domain}/${endpointUrl}?${params}`, request);

  return fetch(`${domain}/${endpointUrl}?${params}`, request)
    .then(setAuthFromHeaders)
    .then((response) => checkStatus(response, controllerKey))
    .then((response) => {
      // console.log(`RESPONSE ${domain}/${endpointUrl}?${params}`, response);
      // eslint-disable-next-line arrow-body-style
      const received = response.json().then((body) => {
        // console.log(`BODY ${domain}/${endpointUrl}?${params}`, body);
        return body;
      });
      return received;
    })
    .catch((error) => {
      throw error;
    });
};

export const getAttachment = (endpointUrl) => {
  const { signal, controllerKey } = generateController();

  const request = {
    method: 'GET',
    headers: requestHeaders(),
    credentials: 'include',
    signal
  };
  return fetch(`/${endpointUrl}`, request)
    .then(setAuthFromHeaders)
    .then((response) => checkStatus(response, controllerKey))
    .then((response) => response.blob())
    .catch((error) => {
      throw error;
    });
};

export const post = (
  endpointUrl,
  data,
  handleUnprocessable = defaultUnprocessableHandler
) => {
  const { signal, controllerKey } = generateController();

  const request = {
    method: 'POST',
    body: data ? JSON.stringify(data) : null,
    headers: requestHeaders(),
    signal
  };
  // console.log(`POST ${domain}/${endpointUrl}`, request);

  return (
    fetch(`${domain}/${endpointUrl}`, request)
      .then(setAuthFromHeaders)
      .then((response) =>
        checkStatus(response, controllerKey, handleUnprocessable)
      )
      // eslint-disable-next-line arrow-body-style
      .then((response) => {
        // console.log(`RESPONSE ${domain}/${endpointUrl}`, response);
        return response;
      })
      .catch((error) => {
        throw error;
      })
  );
};

export const postSkipAuthHeaders = (endpointUrl, data) => {
  const { signal, controllerKey } = generateController();

  const request = {
    method: 'POST',
    body: data ? JSON.stringify(data) : null,
    headers: requestHeaders(),
    signal
  };
  return fetch(`${domain}/${endpointUrl}`, request)
    .then((response) => checkStatus(response, controllerKey))
    .then((response) => response)
    .catch((error) => {
      throw error;
    });
};

export const filePost = (endpointUrl, body, method = 'POST') => {
  const { signal, controllerKey } = generateController();

  const headers = requestHeaders();
  delete headers['content-type'];

  const request = {
    method,
    headers,
    body,
    signal
  };
  return fetch(`${domain}/${endpointUrl}`, request)
    .then(setAuthFromHeaders)
    .then((response) => checkStatus(response, controllerKey))
    .catch((error) => {
      throw error;
    });
};

export const put = (
  endpointUrl,
  data,
  handleUnprocessable = defaultUnprocessableHandler
) => {
  const { signal, controllerKey } = generateController();

  const request = {
    method: 'PUT',
    body: data ? JSON.stringify(data) : null,
    headers: requestHeaders(),
    signal
  };
  // console.log(`PUT ${domain}/${endpointUrl}`, request);

  return fetch(`${domain}/${endpointUrl}`, request)
    .then(setAuthFromHeaders)
    .then((response) =>
      checkStatus(response, controllerKey, handleUnprocessable)
    )
    .then((response) => response)
    .catch((error) => {
      throw error;
    });
};

export const putWithCustomHeaders = (endpointUrl, data, headers) => {
  const { signal, controllerKey } = generateController();

  return (
    fetch(`${domain}/${endpointUrl}`, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : null,
      headers: { ...requestHeaders(), ...headers },
      signal
    })
      .then((response) => checkStatus(response, controllerKey))
      // eslint-disable-next-line arrow-body-style
      .then((response) => {
        // console.log(`RESPONSE ${domain}/${endpointUrl}`, response);
        return response;
      })
      .catch((error) => {
        throw error;
      })
  );
};

export const del = (endpointUrl, data) => {
  const { signal, controllerKey } = generateController();

  const request = {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : null,
    headers: requestHeaders(),
    signal
  };
  // console.log(`DELETE ${domain}/${endpointUrl}`, request);
  return (
    fetch(`${domain}/${endpointUrl}`, request)
      .then(setAuthFromHeaders)
      .then((response) => checkStatus(response, controllerKey))
      // eslint-disable-next-line arrow-body-style
      .then((response) => {
        // console.log(`RESPONSE ${domain}/${endpointUrl}`, response);
        return response;
      })
      .catch((error) => {
        throw error;
      })
  );
};
